import PropTypes from 'prop-types';
import React from 'react';

import ObjectFitImages from 'object-fit-images';

import { CONTRIBUTIONS_SHAPE } from '../../../../config/prop-types';

import getFormattedTotal from '../../services/getFormattedTotal';

/**
 * Displays contrib data for featured lawmaker
 */
export default class Table extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onColumnClick = this.onColumnClick.bind(this);
    this.state = {
      sortedCycle: this.props.data.currentCycle,
      sortOrder: 'DESC',
    };
  }
  componentDidMount() {
    // call the polyfill to support object-fill in IE for profile pic sizing
    ObjectFitImages('img.entity__img');
  }
  /**
   * Handles sort clicks for table headers
   */
  onColumnClick(cycle) {
    const { sortedCycle, sortOrder } = this.state;
    // If the sorted cycle has changed, reset the sort order
    let newSortOrder;
    if (sortedCycle !== cycle) {
      newSortOrder = 'DESC';
    } else {
      newSortOrder = ('DESC' === sortOrder) ? 'ASC' : 'DESC';
    }
    // Dispatch the sort event
    this.props.handleTableSortClick(cycle, newSortOrder);
    this.setState({
      sortedCycle: cycle,
      sortOrder: newSortOrder,
    });
  }
  /**
   * Returns the formatted name string based on entity type
   *
   * @param {object} - The table row data.
   * @returns {object} - JSX for the table row.
   */
  getFormattedName(row) {
    const { type } = this.props;
    if ('MEMBER' === type) {
      return row.company.name;
    } else if ('COMPANY' === type) {
      const { chamber,
              party,
              firstName,
              lastName,
              state } = row.member;
      const honorary = 'House' === chamber ? 'Rep.' : 'Sen.';
      const fmtParty = ['R', 'D', 'I'].includes(party) ? party : '--';
      return `${honorary} ${firstName} ${lastName} (${fmtParty}-${state})`;
    }
    return undefined;
  }
  /**
   * Returns table row JSX for companies and members
   *
   * @param {object} row - The table row data.
   * @returns {object} - JSX for the table row.
   */
  getTableRow(row) {
    const {
      companiesMap,
      data,
      imageBase,
      type,
    } = this.props;
    const entityExists = ('MEMBER' === type) ?
      undefined !== row.company : undefined !== row.member;
    const rowType = ('MEMBER' === type) ? 'COMPANY' : 'MEMBER';
    // Negotiate image URL.
    const imageUrl = 'MEMBER' === type ?
      `${imageBase}/companies/${row.company.id}.jpg` :
      `${imageBase}/members/${row.member.id}.jpg`;
    const company = 'MEMBER' === type ?
      companiesMap.get(row.company.id) || {} : {};
    if (entityExists) {
      return (
        <tr key={'MEMBER' === type ? row.company.id : row.member.id}>
          <th className="entity__name" scope="row">
            <div className="entity__imageheight">
              {('MEMBER' !== type || company.logoExists) && (
                <div className="entity__image">
                  <img
                    className={`entity__img ${rowType}`}
                    src={imageUrl}
                    alt=""
                  />
                </div>
              )}
            </div>
            <span>{ this.getFormattedName(row) }</span>
          </th>
          {data.cycles.map((cycle) => (
            <td className="noMobile" key={cycle}>
              <div className="cell">{getFormattedTotal(row[cycle])}</div>
            </td>
          ))}
          <td className="total">
            <div className="cell">{getFormattedTotal(row.total)}</div>
          </td>
        </tr>
      );
    }
    return undefined; // right now, return undefined for malformed data rows
  }
  /**
   * Returns table row JSX for empty records
   *
   * @returns {object} - JSX for the table row.
   */
  getEmpty() {
    const { data } = this.props;
    return (
      <tr>
        <td
          className="nodata"
          // Add 2 here bc the table has columns for each cycle plus company name and total
          colSpan={data.cycles.length + 2}
        >
          <span className="cell center">
            There are no pharmaceutical contributions on record.
          </span>
        </td>
      </tr>
    );
  }
  sortControl(type) {
    const { sortedCycle, sortOrder } = this.state;
    if (type === sortedCycle && 'ASC' === sortOrder) {
      return (
        <div>
          <div className="upArrow active" />
          <div className="downArrow" />
        </div>
      );
    } else if (type === sortedCycle && 'DESC' === sortOrder) {
      return (
        <div>
          <div className="upArrow" />
          <div className="downArrow active" />
        </div>
      );
    }
    return (<div />);
  }
  /**
   * @returns {object} - JSX for the component.
   */
  render() {
    const {
      data,
      type,
    } = this.props;
    return (
      <div className="campaignFinance__table">
        <table>
          <caption>
            Campaign contributions from pharmaceutical companies
          </caption>
          <thead>
            <tr className="header">
              <th scope="col" id="electionCycle" className="fixwidth">
                <div className="cell">
                  <button
                    className="sort-by"
                    onClick={() =>
                      this.onColumnClick('MEMBER' === type ?
                        'companyName' : 'memberName')}
                  >
                    ELECTION CYCLE
                    {this.sortControl('MEMBER' === type ?
                      'companyName' : 'memberName')}
                  </button>
                </div>
              </th>
              {data.cycles.map((cycle) => (
                <th scope="col" className="noMobile" key={cycle}>
                  <div className="cell">
                    <button
                      className="sort-by"
                      onClick={() => this.onColumnClick(cycle)}
                    >
                      {cycle - 1}-{cycle}
                      {(cycle === data.currentCycle && '*')}
                      {this.sortControl(cycle)}
                    </button>
                  </div>
                </th>
              ))}
              <th scope="col">
                <div className="cell">
                  <div
                    className="sort-by total"
                    onClick={() => this.onColumnClick('total')}
                    role="button"
                    tabIndex="0"
                  >
                    TOTAL
                    {this.sortControl('total')}
                  </div>
                </div>
              </th>
            </tr>
            <tr className="subheader">
              <th scope="row" className="fixwidth pad">
                <div className="cell">TOTAL</div>
              </th>
              {data.cycles.map((cycle) => (
                <td className="noMobile total" key={cycle}>
                  <div className="cell">
                    {getFormattedTotal(data.totals.byCycle[cycle])}
                  </div>
                </td>
              ))}
              <td className="total">
                <div className="cell">
                  {getFormattedTotal(data.totals.grandTotal)}
                </div>
              </td>
            </tr>
          </thead>
          <tbody>
            {0 !== data.rows.length ? (
              data.rows.map((row) => (
                this.getTableRow(row)
              ))
            ) : (
              this.getEmpty()
            )}
            <tr className="padRow" />
          </tbody>
        </table>
        <div className="campaignFinance__table--fadeout" />
      </div>
    );
  }
}

Table.propTypes = {
  companiesMap: PropTypes.instanceOf(Map).isRequired,
  data: PropTypes.shape(CONTRIBUTIONS_SHAPE).isRequired,
  handleTableSortClick: PropTypes.func.isRequired,
  imageBase: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};
